import { CheckCircleOutlined, CloseCircleOutlined, SettingOutlined, SyncOutlined } from "@ant-design/icons";
import {
  Alert, Button, Card, Col, DatePicker, Descriptions, Form,
  Row, Switch, Table, Tag, Tabs, Typography, message, Space,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { transportApi } from "../../api/transportApi";
import { getApiErrorMessage } from "../../utils/apiError";
import { AccountingReportsPanel } from "./AccountingReportsPanel";

type Settings = {
  id: number; isAccountingEnabled: boolean; autoPostEnabled: boolean;
  postingStartDate?: string | null; lockEntriesBefore?: string | null;
  updatedOn?: string; updatedBy?: string;
};

type PostingProfile = {
  id: number; moduleCode: string; transactionTypeCode: string;
  isActive: boolean; isCompiled: boolean; compiledOn?: string | null; ruleCount: number;
};

export const AccountingPage = () => {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [profiles, setProfiles] = useState<PostingProfile[]>([]);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settingsForm] = Form.useForm();

  const loadSettings = async () => {
    setSettingsLoading(true);
    try {
      const s = await transportApi.getAccountingSettings();
      setSettings(s);
      settingsForm.setFieldsValue({
        isAccountingEnabled: s.isAccountingEnabled,
        autoPostEnabled: s.autoPostEnabled,
        postingStartDate: s.postingStartDate ? dayjs(s.postingStartDate) : undefined,
        lockEntriesBefore: s.lockEntriesBefore ? dayjs(s.lockEntriesBefore) : undefined,
      });
    } catch (e) {
      message.error(getApiErrorMessage(e, "Unable to load accounting settings"));
    } finally {
      setSettingsLoading(false);
    }
  };

  const loadProfiles = async () => {
    setProfilesLoading(true);
    try {
      const data = await transportApi.listPostingProfiles();
      setProfiles(data ?? []);
    } catch (e) {
      message.error(getApiErrorMessage(e, "Unable to load posting profiles"));
    } finally {
      setProfilesLoading(false);
    }
  };

  useEffect(() => {
    void loadSettings();
    void loadProfiles();
  }, []);

  const onSaveSettings = async () => {
    const values = await settingsForm.validateFields();
    setSaving(true);
    try {
      await transportApi.updateAccountingSettings({
        isAccountingEnabled: values.isAccountingEnabled,
        autoPostEnabled: values.autoPostEnabled,
        postingStartDate: values.postingStartDate?.toISOString() ?? null,
        lockEntriesBefore: values.lockEntriesBefore?.toISOString() ?? null,
      });
      message.success("Accounting settings saved");
      await loadSettings();
    } catch (e) {
      message.error(getApiErrorMessage(e, "Unable to save settings"));
    } finally {
      setSaving(false);
    }
  };

  const settingsTab = (
    <Row gutter={24}>
      <Col xs={24} md={14}>
        <Form form={settingsForm} layout="vertical">
          <Card
            size="small"
            style={{ marginBottom: 16, borderColor: "var(--app-bg)" }}
            title={
              <Space>
                <SettingOutlined />
                <span>Engine Control</span>
              </Space>
            }
          >
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="isAccountingEnabled"
                  label="Accounting Engine"
                  valuePropName="checked"
                >
                  <Switch
                    checkedChildren="Enabled"
                    unCheckedChildren="Disabled"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="autoPostEnabled"
                  label="Auto-Post Mode"
                  valuePropName="checked"
                  extra="Journal entries post automatically on save"
                >
                  <Switch checkedChildren="Auto" unCheckedChildren="Manual" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="postingStartDate" label="Posting Start Date"
                  extra="Entries before this date are ignored">
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="lockEntriesBefore" label="Lock Entries Before"
                  extra="Prevents modification of past entries">
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>

            <Button type="primary" loading={saving} onClick={onSaveSettings}>
              Save Settings
            </Button>
          </Card>
        </Form>

        {settings?.updatedBy && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Last updated by <strong>{settings.updatedBy}</strong>
            {settings.updatedOn && ` on ${dayjs(settings.updatedOn).format("DD MMM YYYY, HH:mm")}`}
          </Typography.Text>
        )}
      </Col>

      <Col xs={24} md={10}>
        <Alert
          type="info"
          showIcon
          icon={<SettingOutlined />}
          message="Pluggable Accounting Engine"
          description={
            <ul style={{ margin: "8px 0 0", paddingLeft: 16, fontSize: 12 }}>
              <li>The accounting engine is a separate configurable module.</li>
              <li>TransportERP sends domain events; the engine processes them asynchronously.</li>
              <li>Posting profiles define which ledgers are debited/credited per transaction type.</li>
              <li>When disabled, all transactions are recorded but no journal entries are generated.</li>
              <li>In manual mode, a reviewer approves entries before they post to ledgers.</li>
            </ul>
          }
          style={{ marginBottom: 16 }}
        />
        {settings && (
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Engine Status">
              {settings.isAccountingEnabled
                ? <Tag color="green" icon={<CheckCircleOutlined />}>Enabled</Tag>
                : <Tag color="default" icon={<CloseCircleOutlined />}>Disabled</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="Posting Mode">
              {settings.autoPostEnabled
                ? <Tag color="blue" icon={<SyncOutlined />}>Auto-Post</Tag>
                : <Tag>Manual</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="Posting Start">
              {settings.postingStartDate
                ? dayjs(settings.postingStartDate).format("DD MMM YYYY")
                : <Typography.Text type="secondary">Not set</Typography.Text>}
            </Descriptions.Item>
            <Descriptions.Item label="Locked Before">
              {settings.lockEntriesBefore
                ? dayjs(settings.lockEntriesBefore).format("DD MMM YYYY")
                : <Typography.Text type="secondary">Not set</Typography.Text>}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Col>
    </Row>
  );

  const profilesTab = (
    <>
      <Alert
        type="info"
        message="Posting profiles define ledger mappings per module and transaction type. Each profile is compiled by the accounting engine."
        style={{ marginBottom: 16 }}
        showIcon
      />
      <Table
        rowKey="id"
        size="small"
        loading={profilesLoading}
        dataSource={profiles}
        pagination={false}
        columns={[
          { title: "Module", dataIndex: "moduleCode", width: 130 },
          { title: "Transaction Type", dataIndex: "transactionTypeCode" },
          { title: "Rules", dataIndex: "ruleCount", width: 70, align: "right" },
          {
            title: "Compiled", width: 110,
            render: (_, r) => r.isCompiled
              ? <Tag color="green" icon={<CheckCircleOutlined />}>{dayjs(r.compiledOn).format("DD MMM")}</Tag>
              : <Tag color="orange">Pending</Tag>,
          },
          {
            title: "Status", width: 90,
            render: (_, r) => <Tag color={r.isActive ? "blue" : "default"}>{r.isActive ? "Active" : "Inactive"}</Tag>,
          },
        ]}
        locale={{ emptyText: "No posting profiles configured. The accounting engine will create profiles on first use." }}
      />
    </>
  );

  return (
    <Card className="panel-card">
      <Typography.Title level={3} style={{ margin: "0 0 16px" }}>Accounting Engine</Typography.Title>
      <Tabs
        items={[
          { key: "reports", label: "Financial Reports", children: <AccountingReportsPanel /> },
          { key: "settings", label: "Engine Settings", children: settingsTab },
          { key: "profiles", label: "Posting Profiles", children: profilesTab },
        ]}
      />
    </Card>
  );
};
